// Beta-cohort "Send feedback" button. Opens a modal-ish form that POSTs
// to /betaFeedback (Function), which forwards to the beta-feedback Slack
// channel. Visible in the dashboard shell only for teams with is_beta=true.

import { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Button } from '../ui/button.jsx'

const FUNCTIONS_URL =
  import.meta.env.VITE_FUNCTIONS_URL ?? 'https://us-central1-cairn-ebf35.cloudfunctions.net'

async function submit({ teamId, projectId, message, url, getToken }) {
  const token = await getToken()
  const res = await fetch(`${FUNCTIONS_URL}/betaFeedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ teamId, projectId, message, url }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '<no body>')
    throw new Error(`betaFeedback ${res.status}: ${body.slice(0, 200)}`)
  }
  return res.json()
}

export function FeedbackButton({ getToken }) {
  const { teamId, projectId } = useParams()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!message.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await submit({
        teamId,
        projectId,
        message,
        url: window.location.origin + location.pathname,
        getToken,
      })
      setSent(true)
      setMessage('')
      setTimeout(() => {
        setSent(false)
        setOpen(false)
      }, 1500)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        Send feedback
      </Button>
      {open && (
        // biome-ignore lint/a11y/useSemanticElements: popover, not a true <dialog>; no modal scrim
        <div
          role="dialog"
          aria-label="Send beta feedback"
          className="absolute right-0 top-9 z-40 w-80 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-4 shadow-lg"
        >
          <form onSubmit={handleSubmit}>
            <label className="block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
              What's on your mind?
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={4000}
                required
                placeholder="Bug, papercut, idea — anything goes."
                className="mt-1 w-full rounded-md border border-[var(--color-sandstone)] bg-white px-2 py-1.5 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
              />
            </label>
            {error && (
              <p className="mt-2 text-xs text-[var(--color-failed)]" role="alert">
                {error}
              </p>
            )}
            {sent && (
              <output className="mt-2 block text-xs text-[var(--color-passed)]">
                Sent — thanks!
              </output>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button type="submit" variant="primary" size="sm" disabled={submitting || sent}>
                {submitting ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
